import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Card,
  CardContent,
  Button
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as StatusIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import ExportButton from '../../utils/ExportButton';


// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};
function ChangeLogDashboard({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    action_type: '',
    username: ''
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams(filters);
      const response = await axios.get(
        `https://ccc4cs.choicegroup.in/api/change_logs.php?${params}`,
        getAuthConfig()
      );

      if (response.data.success) {
        setLogs(response.data.data || []);
      } else {
        setError('Failed to load change logs');
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError('Failed to load change logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  const handleApplyFilters = () => {
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      action_type: '',
      username: ''
    });
    setTimeout(() => {
      fetchLogs();
    }, 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'EDIT': return 'primary';
      case 'DELETE': return 'error';
      case 'STATUS_CHANGE': return 'warning';
      case 'CREATE': return 'success';
      default: return 'default';
    }
  };

  const renderChangeDetails = (change_details) => {
    if (!change_details || change_details.length === 0) {
      return <Typography variant="body2">No specific changes detected</Typography>;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {change_details.map((detail, idx) => (
          <Paper key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
            {detail.type === 'CARTONS_CHANGED' && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  📢 CARTONS VALUE CHANGED
                </Typography>
                <Typography variant="body2">
                  <strong>Old Value:</strong> {detail.old_value} cartons
                </Typography>
                <Typography variant="body2">
                  <strong>New Value:</strong> {detail.new_value} cartons
                </Typography>
              </Box>
            )}

            {detail.type === 'BIN_LOCATIONS_CHANGED' && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  📦 BIN LOCATIONS CHANGED
                </Typography>
                {detail.changes.cs1 && (
                  <Typography variant="body2">
                    <strong>CS1:</strong> {detail.changes.cs1.old} → {detail.changes.cs1.new}
                  </Typography>
                )}
                {detail.changes.cs2 && (
                  <Typography variant="body2">
                    <strong>CS2:</strong> {detail.changes.cs2.old} → {detail.changes.cs2.new}
                  </Typography>
                )}
                {detail.changes.pallet_id && (
                  <Typography variant="body2">
                    <strong>Pallet ID:</strong> {detail.changes.pallet_id.old} → {detail.changes.pallet_id.new}
                  </Typography>
                )}
              </Box>
            )}

            {detail.type === 'STATUS_CHANGED' && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  🔄 STATUS CHANGED
                </Typography>
                <Typography variant="body2">
                  <strong>Previous Status:</strong> <Chip label={detail.previous_status} size="small" color="default" />
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Current Status:</strong> <Chip label={detail.current_status} size="small" color="success" />
                </Typography>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  // Separate logs by action type
  const editLogs = logs.filter(log => log.action_type === 'EDIT');
  const deleteLogs = logs.filter(log => log.action_type === 'DELETE');
  const statusChangeLogs = logs.filter(log => log.action_type === 'STATUS_CHANGE');

  const renderLogTable = (title, logData, icon, color) => {
    return (
      <Accordion defaultExpanded={logData.length > 0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title} ({logData.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {logData.length === 0 ? (
            <Alert severity="info">No {title.toLowerCase()} found</Alert>
          ) : (
            <Box>
              {logData.map((log) => (
                <Paper key={log.id} sx={{ mb: 2, border: `2px solid ${color}` }}>
                  <Box sx={{ p: 2, bgcolor: `${color}15` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip 
                          label={log.action_type || log.action} 
                          size="small" 
                          color={getActionColor(log.action_type)}
                        />
                        <Typography sx={{ fontWeight: 600 }}>
                          {log.full_name || log.username}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(log.created_at)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {log.action_details || log.action}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Change Information
                          </Typography>
                          <Typography variant="body2">
                            <strong>Inventory ID:</strong> {log.inventory_id || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>User:</strong> {log.full_name || log.username}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Action:</strong> {log.action}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Timestamp:</strong> {formatDate(log.created_at)}
                          </Typography>
                          {log.reason && (
                            <Typography variant="body2">
                              <strong>Reason:</strong> {log.reason}
                            </Typography>
                          )}
                          {log.approved_by_username && (
                            <Typography variant="body2">
                              <strong>Approved By:</strong> {log.approved_by_username}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, bgcolor: 'info.lighter' }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Specific Changes
                          </Typography>
                          {renderChangeDetails(log.change_details)}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            📋 Change Log
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Track all changes made to inventory records
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ExportButton 
            exportType="change_logs" 
            label="Export Logs" 
          />
          <IconButton onClick={fetchLogs} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Total Logs</Typography>
              <Typography variant="h3">{logs.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Edit Logs</Typography>
              <Typography variant="h3">{editLogs.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Delete Logs</Typography>
              <Typography variant="h3">{deleteLogs.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Status Change Logs</Typography>
              <Typography variant="h3">{statusChangeLogs.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              label="Action Type"
              value={filters.action_type}
              onChange={(e) => handleFilterChange('action_type', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="EDIT">Edit</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
              <MenuItem value="STATUS_CHANGE">Status Change</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Username"
              value={filters.username}
              onChange={(e) => handleFilterChange('username', e.target.value)}
              placeholder="Search user..."
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
              >
                Apply
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Edit Logs */}
          {renderLogTable('Edit Logs', editLogs, <EditIcon sx={{ color: 'primary.main' }} />, '#1565c0')}
          
          {/* Delete Logs */}
          {renderLogTable('Delete Logs', deleteLogs, <DeleteIcon sx={{ color: 'error.main' }} />, '#d32f2f')}
          
          {/* Status Change Logs */}
          {renderLogTable('Status Change Logs', statusChangeLogs, <StatusIcon sx={{ color: 'warning.main' }} />, '#ed6c02')}
        </Box>
      )}

      {/* No Data State */}
      {!loading && logs.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No change logs found
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default ChangeLogDashboard;